import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useRouteLoaderData
} from 'react-router-dom'
import invariant from 'tiny-invariant'

import {
  Column,
  Container,
  DeviationsTable,
  EmptyScreen,
  EmptyState,
  Row
} from '@cdab/scania/qpr/components'
import { useFeatures } from '@cdab/scania/qpr/feature-flags'
import type { Deviation } from '@cdab/scania/qpr/schema'
import { useDoOnce } from '@cdab/utils'
import { TdsButton, TdsIcon } from '@scania/tegel-react'
import styled from 'styled-components'
import type { AuditLoaderData } from '../../../../../auditId'

const Content = styled.div`
  height: 100%;
  padding-bottom: var(--tds-spacing-element-40);
`

const ScrollableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
`

type Params = {
  auditId: string
  auditPointId: string
}

export const DeviationsTab = observer((): JSX.Element => {
  const { t } = useTranslation()
  const { pathname, state: stateLocation } = useLocation()
  const navigate = useNavigate()

  const { draft_deviations } = useFeatures(['draft_deviations'])

  const params = useParams<Params>()
  const { auditPointId: sAuditPointId } = params
  const auditId = Number(params.auditId)

  const { audit } = useRouteLoaderData('audit') as AuditLoaderData
  const { deviations, auditPoints, isReadonly } = audit

  const auditPointId = Number.parseInt(sAuditPointId ?? 'NaN')
  invariant(auditId, 'auditId is required')
  invariant(!Number.isNaN(auditPointId), 'auditPointId is required')

  const deviationsForCurrentAuditPoint = deviations.filter(
    deviation => deviation.auditPointId === auditPointId
  )

  const draftDeviations = deviationsForCurrentAuditPoint.filter(d => d.draft)
  const deviationsNotDraft = deviationsForCurrentAuditPoint.filter(
    d => !d.draft
  )

  const auditPoint = auditPoints.find(ap => ap.id === auditPointId)
  invariant(auditPoint, `Could not find audit point with id ${auditPointId}`)

  const handleClickDeviation = useCallback(
    (guid: Deviation['clientGuid']) => {
      const deviation = deviations.find(({ clientGuid }) => clientGuid === guid)
      invariant(deviation)
      if (deviation.draft) {
        navigate(
          `/audit/${auditId}/points/auditpoint/${auditPointId}/deviations/new`,
          {
            state: { clientGuid: deviation.clientGuid }
          }
        )
      } else {
        navigate(
          `/audit/${auditId}/points/auditpoint/${auditPointId}/deviations/${deviation.clientGuid}`
        )
      }
    },
    [auditId, auditPointId, deviations, navigate]
  )

  const handleClickCreateDeviation = useCallback(() => {
    navigate(
      `/audit/${auditId}/points/auditpoint/${auditPointId}/deviations/new`
    )
  }, [auditId, auditPointId, navigate])

  useDoOnce(() => {
    if (stateLocation && 'deviationId' in stateLocation) {
      const deviationGuid = deviations.find(
        ({ id }) => id?.toString() === stateLocation.deviationId
      )?.clientGuid

      if (deviationGuid)
        navigate(
          `/audit/${auditId}/points/auditpoint/${auditPointId}/deviations/${deviationGuid}`,
          {
            state:
              'previousPath' in stateLocation
                ? { previousPath: stateLocation.previousPath }
                : undefined
          }
        )
    }
  })

  if (
    pathname ===
    `/audit/${auditId}/points/auditpoint/${auditPointId}/deviations/new`
  ) {
    return <Outlet />
  }

  if (deviationsForCurrentAuditPoint.length === 0) {
    return (
      <Content>
        <EmptyScreen.CenteredWrapper>
          <EmptyScreen
            icon={<TdsIcon name='message_inactive' size='lg' />}
            title={t('no-deviations-found')}
            description={t('no-deviations-found-description')}
            callToAction={
              !isReadonly
                ? {
                    type: 'button',
                    component: (
                      <TdsButton
                        size='sm'
                        onClick={() => {
                          !isReadonly && handleClickCreateDeviation()
                        }}
                        disabled={isReadonly}
                        text={t('create-deviation', { ns: 'audit' })}
                      />
                    )
                  }
                : undefined
            }
          />
        </EmptyScreen.CenteredWrapper>
      </Content>
    )
  }

  const showDeviations =
    (draft_deviations.enabled && deviationsForCurrentAuditPoint.length > 0) ||
    (!draft_deviations.enabled && deviationsNotDraft.length > 0)

  if (
    pathname ===
    `/audit/${auditId}/points/auditpoint/${auditPointId}/deviations`
  ) {
    return (
      <Content>
        <Container>
          <Row className='tds-u-pt3 sdds-u-pb2'>
            {!audit.isReadonly && (
              <Column width={12}>
                <TdsButton
                  size='sm'
                  onClick={() => !isReadonly && handleClickCreateDeviation()}
                  disabled={isReadonly}
                  text={t('create-deviation', { ns: 'audit' })}
                />
              </Column>
            )}
          </Row>
          <Row>
            <Column width={12}>
              {showDeviations ? (
                <>
                  {draft_deviations.enabled && draftDeviations.length > 0 && (
                    <Row className='tds-u-mb2'>
                      <ScrollableContainer>
                        <DeviationsTable
                          id='deviations-table-draft'
                          deviations={draftDeviations}
                          onRowClick={handleClickDeviation}
                          name='Draft deviations'
                        />
                      </ScrollableContainer>
                    </Row>
                  )}
                  {deviationsNotDraft.length > 0 && (
                    <Row>
                      <ScrollableContainer>
                        <DeviationsTable
                          id='deviations-table'
                          deviations={deviationsNotDraft}
                          onRowClick={handleClickDeviation}
                          name={'Deviations'}
                        />
                      </ScrollableContainer>
                    </Row>
                  )}
                </>
              ) : (
                // TODO: https://linear.app/congenialdata/issue/QPR-485/empty-screen-templates
                <EmptyState title={t('no-deviations')} />
              )}
            </Column>
          </Row>
        </Container>
      </Content>
    )
  } else {
    return <Outlet />
  }
})
